import slugify from 'slugify';
import getGraphQlClient from '~graphql/getGraphQlClient';
import {
  CREATE_EDITOR_BACKGROUND,
  CREATE_EDITOR_BACKGROUND_DEFAULT,
} from '~graphql/queries/editorBackgrounds';
import { getUser, isLoggedUserGlobalEditor } from '~src/user';

export const BACKGROUND_COLLECTION = 'editor_backgrounds';
export const BACKGROUND_DEFAULT_COLLECTION = 'editor_backgrounds_default';

const CONTENT_TYPE_EXTENSION = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
};

const EXTENSION_CONTENT_TYPE = {
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

export const getBackgroundOrientation = (width, height) => {
  if (width === height) {
    return 's';
  }

  return width > height ? 'h' : 'v';
};

export const normalizeTags = (tags = []) => {
  if (Array.isArray(tags)) {
    return tags.filter(Boolean);
  }

  if (typeof tags === 'string' && tags) {
    return [tags];
  }

  return [];
};

export const getContentTypeFromFileName = (fileName) => {
  const extension = String(fileName || '')
    .split('.')
    .pop()
    .toLowerCase();

  return EXTENSION_CONTENT_TYPE[extension] || 'application/octet-stream';
};

const getFileExtensionFromContentType = (contentType) =>
  CONTENT_TYPE_EXTENSION[contentType] || 'bin';

export const getUploadFileName = (fileName, contentType) => {
  const cleanFileName = slugify(fileName || 'background');

  if (/\.[a-z0-9]+$/i.test(cleanFileName)) {
    return cleanFileName;
  }

  return `${cleanFileName}.${getFileExtensionFromContentType(contentType)}`;
};

const normalizeUrlWithProtocol = (url) => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `https://${url}`;
};

const getEdgeResizerBase = (edgeResizer = process.env.EDGE_RESIZER) => {
  if (!edgeResizer) {
    throw new Error('EDGE_RESIZER non configurato');
  }

  return normalizeUrlWithProtocol(edgeResizer).replace(/\/+$/, '');
};

const getRootS3Key = (s3Key) => {
  const key = String(s3Key || '').replace(/^\/+|\/+$/g, '');

  if (!key || key.includes('/') || /%2f/i.test(key)) {
    throw new Error(
      'Gli sfondi devono essere salvati nella root del bucket S3'
    );
  }

  return key;
};

export const buildEdgeUrlFromS3Key = (
  s3Key,
  edgeResizer = process.env.EDGE_RESIZER
) =>
  `${getEdgeResizerBase(edgeResizer)}/${encodeURIComponent(
    getRootS3Key(s3Key)
  )}`;

export const getS3KeyFromUrl = (url) => {
  const { pathname } = new URL(url);

  return decodeURIComponent(pathname.replace(/^\/+/, ''));
};

export const buildEdgeUrlFromPathname = (
  pathname,
  edgeResizer = process.env.EDGE_RESIZER
) =>
  buildEdgeUrlFromS3Key(
    decodeURIComponent(pathname.replace(/^\/+/, '')),
    edgeResizer
  );

export const isEdgeResizerUrl = (
  url,
  edgeResizer = process.env.EDGE_RESIZER
) => {
  try {
    return (
      new URL(url).origin === new URL(getEdgeResizerBase(edgeResizer)).origin
    );
  } catch (error) {
    return false;
  }
};

export const buildParseFileInput = ({ name, url }) => ({
  file: {
    __type: 'File',
    name,
    url,
  },
});

export const buildBackgroundFields = ({
  fileName,
  fileUrl,
  width,
  height,
  tags = [],
  isGlobal = false,
}) => {
  const fields = {
    file: buildParseFileInput({ name: fileName, url: fileUrl }),
    height,
    orientation: getBackgroundOrientation(width, height),
    width,
  };

  const tagIds = normalizeTags(tags);
  if (tagIds.length) {
    fields.tags = { add: tagIds };
  }

  if (isGlobal) {
    fields.demo = true;
    fields.isGlobal = true;
  }

  return fields;
};

export const createBackgroundRecord = async ({
  fileName,
  fileUrl,
  width,
  height,
  tags = [],
  isGlobal = false,
}) => {
  const graphQlClient = await getGraphQlClient();
  const fields = buildBackgroundFields({
    fileName,
    fileUrl,
    height,
    isGlobal,
    tags,
    width,
  });

  const mutation = isGlobal
    ? CREATE_EDITOR_BACKGROUND_DEFAULT
    : CREATE_EDITOR_BACKGROUND;
  const response = await graphQlClient.mutate({
    mutation,
    variables: { fields },
  });

  if (isGlobal) {
    return response.data.createEditor_backgrounds_default
      .editor_backgrounds_default;
  }

  return response.data.createEditor_backgrounds.editor_backgrounds;
};

export const createBackgroundRecordForCurrentUser = async (params) => {
  const isGlobal = await isLoggedUserGlobalEditor();

  return createBackgroundRecord({
    ...params,
    isGlobal,
  });
};

export const createBackgroundRecordFromUrl = async ({
  imageUrl,
  width,
  height,
  tags = [],
}) => {
  if (!isEdgeResizerUrl(imageUrl)) {
    throw new Error('Lo sfondo generato non arriva dal CDN Parse configurato');
  }

  const s3Key = getRootS3Key(getS3KeyFromUrl(imageUrl));

  return createBackgroundRecordForCurrentUser({
    fileName: s3Key,
    fileUrl: imageUrl,
    height,
    tags,
    width,
  });
};

export const requestUploadParameters = async ({
  fileName,
  contentType,
  collection,
}) => {
  const { userToken } = await getUser();
  const response = await fetch(
    `${process.env.PARSE_SERVER_ENDPOINT}/functions/upload`,
    {
      body: JSON.stringify({
        bucket: 'default',
        collection,
        contentType,
        dir: '',
        filename: fileName,
      }),
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-parse-application-id': process.env.PARSE_APPLICATION_ID,
        'x-parse-session-token': userToken,
      },
      method: 'post',
    }
  );

  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok) {
    throw new Error(
      data.error || data.message || 'Impossibile ottenere URL per upload'
    );
  }

  if (!data.result || !data.result.url) {
    throw new Error('Impossibile ottenere URL per upload');
  }

  return data.result;
};

export const uploadFileToPresignedUrl = async ({
  file,
  uploadParameters,
  contentType,
}) => {
  const response = await fetch(uploadParameters.url, {
    body: file,
    headers: {
      'Content-Type': contentType,
    },
    method: uploadParameters.method || 'PUT',
  });

  if (!response.ok) {
    throw new Error('Upload sfondo fallito');
  }
};

export const uploadBackgroundFile = async ({
  file,
  width,
  height,
  tags = [],
}) => {
  const isGlobal = await isLoggedUserGlobalEditor();
  const collection = isGlobal
    ? BACKGROUND_DEFAULT_COLLECTION
    : BACKGROUND_COLLECTION;
  const contentType =
    file.type || getContentTypeFromFileName(file.name || 'background');
  const fileName = getUploadFileName(file.name, contentType);
  const uploadParameters = await requestUploadParameters({
    collection,
    contentType,
    fileName,
  });

  await uploadFileToPresignedUrl({
    contentType,
    file,
    uploadParameters,
  });

  const s3Key = getRootS3Key(
    uploadParameters.s3key || getS3KeyFromUrl(uploadParameters.url)
  );
  const fileUrl = buildEdgeUrlFromS3Key(s3Key);

  return createBackgroundRecord({
    fileName: s3Key,
    fileUrl,
    height,
    isGlobal,
    tags,
    width,
  });
};
